import { useState } from 'react';
import { MoreVertical, Plus, Inbox, Move, Trash2, User, Scissors, Merge, RotateCcw, Calendar, Car, Ban, Clock, Phone, CheckCircle2, ArrowRightLeft, PackageX, Wrench, UserRound, UserCog } from 'lucide-react';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import {
  useTicketCustody,
  useTicketCustodyMutation,
  primaryCustodyAction,
} from '@/hooks/tickets/useTicketCustody';
import { DeletionReasonDialog } from './DeletionReasonDialog';
import { EditTicketDetailsModal } from './EditTicketDetailsModal';
import { ReassignAdvisorDialog } from './ReassignAdvisorDialog';
import {
  CustodyHandoffReasonDialog,
  type CustodyHandoffReasonDialogResult,
} from './CustodyHandoffReasonDialog';
import { buildCustodyHandoffMetadata } from '@/lib/tickets/lifecycle/custodyHandoffReasons';
import { TicketTransferDialog } from '@/components/teams/TicketTransferDialog';
import { ParkingLotReasonDialog } from '@/components/scheduling/ParkingLotReasonDialog';
import type { ParkingLotReason } from '@/constants/parkingLotReasons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { InterruptedRepairReadyItem } from '@/components/tickets/InterruptedRepairReadyItem';
import { useDate } from '@/contexts/DateContext';
import { format } from 'date-fns';
import { CalendarModal } from '@/components/layout/CalendarModal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { applyEligibility, fetchTicketEligibleTechnicianIds } from '@/lib/roles/eligibilityQuery';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { formatShopTime } from '@/utils/formatShopTime';
import { getShopDayLabel } from '@/utils/shopTime';
import { usePermissions } from '@/hooks/usePermissions';
import { useMultiTeam } from '@/hooks/useMultiTeam';
import { 
  getJobsNeedingOutcomeForModal, 
  syncDiagnosticOutcomeFlag 
} from '@/utils/diagnosticJobDetection';
import { triggerBoardCascade } from '@/utils/cascadeTrigger';
import { mutateTicketSchedule } from '@/utils/mutateTicketSchedule';
import { isVehiclePresent } from '@/utils/deriveVehiclePresence';
// DMC-D3c-3: canonical deleted-state helper. Mirrors public.is_ticket_deleted
// (SQL) from migration 20260508120000_dmc_d2_deleted_state_helpers.sql.
import { isTicketDeleted } from '@/utils/ticketDeletedState';
import { logTicketEventToOcis } from '@/utils/ocisIngest';
// DTC-G G1: route lifecycle write timestamps + reminder send_at delays
// through the canonical clock so the kebab-menu's persisted writes are
// deterministic under `clock.setFixed(...)`. Same drift class as DTC-A
// (timer-state writes) and DTC-F V8 (reminder send_at delays).
/**
 * 8G.2C.9 §9: the reminders a customer departure should suppress.
 *
 * Positive scope, deliberately. The pickup CONSEQUENCES —
 * `picked_up_followup` and `escalation` — are absent because they exist
 * because the customer has the vehicle; cancelling them was the measured
 * defect. A new reminder type is suppressed only when someone adds it here.
 */
import {
  classifyEnqueue, classifySuppression, classifyCascade,
  allConsequencesOk, describeConsequences,
  type PickupConsequences,
} from '@/utils/pickupConsequenceResults';

const PRE_PICKUP_REMINDER_TYPES = [
  'immediate', '24h', '2h', 'custom', 'appointment', 'paused_followup',
] as const;

import { clock } from '@/utils/clock';
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';
import { notifyPickupChanged } from '@/utils/pickupConvergence';
import { useAuth } from '@/contexts/AuthContext';
import { ParkingSpotPickerDialog } from '@/components/birdseye/ParkingSpotPickerDialog';
// Oct 20 (FD-06 / D-9 / FD-01): the shared incomplete-pickup reason dialog and the one Move Vehicle dialog.
import { IncompletePickupDialog } from '@/components/tickets/IncompletePickupDialog';
import { FohMoveVehicleDialog } from '@/components/foh/workboard/FohMoveVehicleDialog';
import {
  shouldPromptForDeleteLocation,
  toTerminalLocation,
  type TerminalLocationChoice,
} from '@/lib/tickets/deleteLocationPrompt';
// OCT20 WS-ROLL (FD-04): a day of a multi-day rollover is deleted only with its whole repair.
import { RolloverChainDeleteDialog } from '@/components/tickets/RolloverChainDeleteDialog';
import type { ChainDeleteResult } from '@/utils/deleteRolloverChain';
// FLOYD 2026-10-26 (DEFECT 1): static — the delete action ships inside the menu that offers it (it used to be a
// lazily loaded chunk that a deploy removed from under an open tab), and a chunk failure says so in words.
import {
  deleteTicketFamily,
  describeFamilyDeleteRefusal,
  didDeleteFamily,
  isFamilyDeleteSettled,
} from '@/utils/deleteTicketFamily';
import { APP_UPDATED_MESSAGE, isChunkLoadError } from '@/lib/app/chunkLoadRecovery';
import { reloadToastAction } from '@/components/app/AppUpdateRequired';

interface TicketKebabMenuProps {
  ticketId: string;
  isInParkingLot: boolean;
  technicians: Array<{ id: string; name: string; is_present: boolean; hours?: any; time_off?: string[] }>;
  assignedTechId?: string;
  jobCount?: number;
  isSplitTicket?: boolean;
  status?: string;
  vehiclePresent?: boolean;
  ticketType?: 'Waiter' | 'Drop-off';
  scheduledStartTime?: string | null;
  dueOut?: string | null;
  isComeback?: boolean;
  predictedMinutes?: number;
  timerStatus?: string;
  needsDiagnosticOutcome?: boolean;
  confirmationStatus?: string | null;
  teamId?: string;
  onAddJob?: (ticketId: string) => void;
  onDelete?: (ticketId: string) => void;
  onSplit?: (ticketId: string) => void;
  onMerge?: (ticketId: string) => void;
  onReassign?: () => void;
}

export const TicketKebabMenu = ({ 
  ticketId, 
  isInParkingLot, 
  technicians,
  assignedTechId,
  jobCount = 0,
  isSplitTicket = false,
  status,
  vehiclePresent,
  ticketType = 'Drop-off',
  scheduledStartTime = null,
  dueOut = null,
  isComeback = false,
  predictedMinutes = 0,
  timerStatus,
  needsDiagnosticOutcome = false,
  confirmationStatus,
  teamId,
  onAddJob,
  onDelete,
  onSplit,
  onMerge,
  onReassign
}: TicketKebabMenuProps) => {
  const { toast } = useToast();
  const { selectedDate: currentDate, selectedDayLabel } = useDate();
  const { activeShop } = useAuth();
  const shopId = activeShop?.id || '';
  const queryClient = useQueryClient();
  const { shopTimezone } = useShopTimezone();
  const { canRecordDiagnosticOutcome, canEditTickets, canAssignTickets, canDeleteTickets, canRestoreTickets, canReassignAdvisor, canRelocateVehicles } = usePermissions();
  // Oct 20: the reason dialog for Picked Up (Incomplete) (FD-06), and the physical Move Vehicle dialog (D-9).
  const [showIncompletePickupDialog, setShowIncompletePickupDialog] = useState(false);
  const [incompletePickupBusy, setIncompletePickupBusy] = useState(false);
  const [showMoveVehicleDialog, setShowMoveVehicleDialog] = useState(false);
  const { isMultiTeamEnabled } = useMultiTeam();
  const [isOpen, setIsOpen] = useState(false);
  const [showDeletionDialog, setShowDeletionDialog] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showParkingLotReasonDialog, setShowParkingLotReasonDialog] = useState(false);
  const [pendingParkingLotReason, setPendingParkingLotReason] = useState<ParkingLotReason | null>(null);
  // FLOYD-5 — held across the location round trip. The operator already chose
  // a deletion reason; the server then asked where the car goes. Losing the
  // reason would make them pay twice for one decision.
  const [pendingDeleteReason, setPendingDeleteReason] = useState<string | null>(null);
  const [showDeleteLocationPicker, setShowDeleteLocationPicker] = useState(false);
  // OCT20 WS-ROLL (FD-04): the server said this ticket is one day of a rollover → the whole-repair warning.
  const [chainDeleteReason, setChainDeleteReason] = useState<string | null>(null);
  // Slice 5D-1: explicit advisor reassignment dialog state. Gated by
  // custodyHandoff + custody_owner='FOH' + canReassignAdvisor below; the
  // state itself is harmless when those gates are off.
  const [showReassignAdvisorDialog, setShowReassignAdvisorDialog] = useState(false);

  // Batch 1 (FOH timer handoff): manual kick-to-advisor / kick-back-to-tech
  // reason picker dialog state. Replaces the slice 3.6 placeholder which
  // sent every manual kick through with `reason: null`. The picker uses
  // the canonical reason sets in custodyHandoffReasons.ts and the actual
  // custody mutation runs inside the dialog's onConfirm handler.
  const [showCustodyReasonDialog, setShowCustodyReasonDialog] = useState(false);
  
  // Diagnostic outcome modal state
  const [showDiagnosticModal, setShowDiagnosticModal] = useState(false);
  const [pendingDiagnosticJobs, setPendingDiagnosticJobs] = useState<Array<{ jobId: string; jobName: string }>>([]);
  
  // Lane 3 confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<'parking_lot' | null>(null);
  
  // Available technicians are those present (no date filtering here since we want all present techs)
  const presentTechnicians = technicians.filter(tech => tech.is_present);

  // Core-100 (multi-role): the Move list used to offer every present technician, including ones
  // the assignment trigger refuses for this ticket's work (a Detail Specialist for a brake job).
  // The move then failed AFTER the choice. Ask the server's own rule while the menu is open;
  // when that answer is unavailable (null) the list stays whole and the server still refuses.
  const { data: eligibleTechIds = null } = useQuery({
    queryKey: ['ticket-eligible-technicians', ticketId],
    queryFn: () => fetchTicketEligibleTechnicianIds(ticketId),
    enabled: isOpen && canAssignTickets && !!ticketId,
    staleTime: 30_000,
  });
  const availableTechnicians = applyEligibility(presentTechnicians, eligibleTechIds);

  // Handler for diagnostic outcome interception
  const checkDiagnosticAndExecute = async (action: 'parking_lot', executeAction: () => Promise<void>) => {
    // CORE-I: this surface may report that the competency evidence is
    // MISSING; it may not supply it. The outcome is the performing
    // technician's assertion, made on the authenticated diagnostic-capture
    // path — `core_i_competency_writer_trg` refuses any other writer.
    if (needsDiagnosticOutcome) {
      const unresolvedJobs = await getJobsNeedingOutcomeForModal(ticketId);

      if (unresolvedJobs.length > 0) {
        setIsOpen(false);
        toast({
          title: 'Diagnostic outcome still required',
          description:
            'The technician who performed the diagnostic work records what it '
            + 'found, from their timer.',
          variant: 'destructive',
        });
        return;
      }
    }
    
    // No interception needed, execute directly
    await executeAction();
  };


  // The actual parking lot move logic (executed after diagnostic check)
  const executeParkingLotMove = async (reason: ParkingLotReason | string = 'other') => {
    console.log('[PARKING LOT] executeParkingLotMove called for ticket:', ticketId);
    
    try {
      // Check if this is part of a rollover chain
      const { data: ticket } = await supabase
        .from('tickets')
        .select('parent_ticket_id, actually_started, timer_status')
        .eq('id', ticketId)
        .maybeSingle();

      // RULEBOOK FIX 1: Block parking lot move if timer is ACTIVELY running
      // Only timer_status === 'running' blocks - paused/stopped tickets CAN go to parking lot
      if (ticket?.timer_status === 'running') {
        toast({
          title: 'Cannot Move to Parking Lot',
          description: 'Stop or pause the timer before moving this ticket to the Parking Lot.',
          variant: 'destructive',
        });
        setIsOpen(false);
        return;
      }

      const isRolloverPart = ticket?.parent_ticket_id !== null;

      if (isRolloverPart) {
        // Chronomancer compliance: rollover merge is not allowed — it bypasses
        // chain identity, cascade behavior, and rollover type safety.
        toast({
          title: 'Cannot Move Rollover to Parking Lot',
          description: 'Rollover tickets cannot be merged. Use the standard completion flow instead.',
          variant: 'destructive',
        });
        setIsOpen(false);
        return;
      }

      // Normal parking lot move — route through reassign-ticket for atomic handling
      // (Pensieve logging, split-sibling chain-follow, OCIS, bay cleanup, cascade)
      const { error } = await supabase.functions.invoke('reassign-ticket', {
        body: {
          ticketId,
          targetTechId: null,
          scheduledDate: selectedDayLabel,
          moveToParkingLot: true,
          parkingLotReason: reason,
          moveReason: 'manual-kebab-to-parking-lot',
        },
      });

      if (error) throw error;

      // Invalidate ticket history to show Pensieve event immediately
      queryClient.invalidateQueries({ queryKey: ['ticket-history', ticketId] });

      // Cross-device Dayboard sync
      void notifyDayboardInvalidated({
        reason: 'kebab-parking-lot',
        shopId,
        ticketId,
        scheduledDate: selectedDayLabel,
        metadata: { parkingLotReason: reason },
      });

      toast({
        title: 'Moved to Parking Lot',
        // Oct 20 (D-9): this is a scheduling act — say so. The physical move is "Move Vehicle".
        description: 'Taken off the technician\'s schedule. The vehicle itself was not moved — use Move Vehicle to record where it is.',
      });
    } catch (error: any) {
      console.error('[PARKING LOT] Error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to move ticket to parking lot',
        variant: 'destructive',
      });
    }
    setIsOpen(false);
  };

  // Show parking lot reason dialog first, then proceed with diagnostic check + move
  const handleMoveToParkingLot = () => {
    setShowParkingLotReasonDialog(true);
    setIsOpen(false);
  };

  const handleParkingLotReasonConfirm = async (reason: ParkingLotReason) => {
    setPendingParkingLotReason(reason);
    await checkDiagnosticAndExecute('parking_lot', () => executeParkingLotMove(reason));
  };

  const handleMoveTechnician = async (techId: string, techName: string) => {
    try {
      // R7-L-3: Use canonical selectedDayLabel, not UTC split
      const scheduledDate = selectedDayLabel;
      
      // Call reassign-ticket edge function to handle EFS + normalization
      const { data, error } = await supabase.functions.invoke('reassign-ticket', {
        body: {
          ticketId,
          targetTechId: techId,
          scheduledDate,
          manualOverride: true,
          moveReason: 'manual-kebab-tech-change',
        }
      });

      if (error) throw error;

      const cascadedCount = data?.cascadedCount || 0;
      const movedPartsCount = data?.movedPartsCount || 1;
      const cascadeMsg = cascadedCount > 0 ? ` (cascaded ${cascadedCount} tickets)` : '';
      const newStartDate = new Date(data.newStartTime);
      const startTime = formatShopTime(data.newStartTime, shopTimezone, 'short');
      
      // R7-L-3: Prefer scheduled_date from response; fallback via shop TZ
      const newDateStr = data.scheduled_date ?? getShopDayLabel(data.newStartTime, shopTimezone);
      const currentDateStr = selectedDayLabel;
      const dateMsg = newDateStr !== currentDateStr 
        ? ` on ${format(newStartDate, 'MMM dd')}` 
        : '';

      // Build message based on whether multiple parts were moved
      const partsMsg = movedPartsCount > 1 ? ` (${movedPartsCount} parts)` : '';

      // Invalidate queries to refetch tickets and update the board
      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
      await queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });

      // Cross-device Dayboard sync
      void notifyDayboardInvalidated({
        reason: 'kebab-reassign',
        shopId,
        ticketId,
        scheduledDate: newDateStr,
        metadata: { targetTechId: techId, targetTechName: techName },
      });

      toast({
        title: movedPartsCount > 1 ? 'Split Ticket Reassigned' : 'Ticket Reassigned',
        description: `Moved${partsMsg} to ${techName} at ${startTime}${dateMsg}${cascadeMsg}`,
      });
      
      // Notify parent that a reassignment occurred (for carousel reInit)
      onReassign?.();
    } catch (error: any) {
      console.error('[REASSIGN] Error:', error);
      toast({
        title: 'Reassignment Failed',
        description: error.message || 'Failed to reassign ticket',
        variant: 'destructive',
      });
    }
    setIsOpen(false);
  };

  /**
   * FLOYD-5 — `location` is supplied only on the RETRY, after the server has
   * said the car is still in a bay and the operator has answered where it
   * goes. The first attempt deliberately sends none: most tickets are already
   * parked, and asking every time would tax the common case to serve the rare
   * one.
   */
  const handleDelete = async (
    reason: string,
    location: TerminalLocationChoice | null = null,
  ) => {
    // FLOYD 2026-10-26 (DEFECT 1): which side of the request a failure falls on decides what the person is told.
    // Before the request nothing can have been deleted; after it, only the server's answer says what happened —
    // and once the server has deleted, a failed follow-up (timeline, schedule re-walk) is not a failed delete.
    let requestSent = false;
    try {
      // 8G.2C: THE PRE-DELETE PAUSE MUTATION IS GONE.
      //
      // This used to begin with `closePauseSession(ticketId)`, which is
      // ticket-global and technician-blind: it selects the NEWEST open pause
      // by ticket_id alone and writes `resumed_at` plus `duration_minutes`.
      //
      // MEASURED with Tech A and Tech B both paused: it targeted TECH A's
      // pause and marked it resumed — a manager's delete click asserting that
      // a technician came back to the vehicle — and only AFTER that did the
      // authority correctly answer PAUSED_WORK. The refusal was already too
      // late; one technician's evidence had been destroyed to make room for
      // an act that never happened.
      //
      // The authority is now the first and only thing this path calls.

      // Fetch the ticket data before deletion for cascade recalculation
      const { data: ticketToDelete, error: fetchError } = await supabase
        .from('tickets')
        .select('start_time, assigned_tech_id, scheduled_date, shop_id')
        .eq('id', ticketId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      // 8G.2D.4A: THE BROWSER IS NO LONGER THE TRANSACTION COORDINATOR.
      //
      // This used to read the descendants here and cancel them one at a time:
      //
      //   const results = await Promise.allSettled(
      //     descendants.map(d => cancelTicketChain(d.id, 'parent-deleted')));
      //   if (failures.length > 0) { toast('… Deletion aborted.'); return; }
      //
      // `allSettled` does not short-circuit, so every descendant was attempted
      // and each cancelTicketChain committed on its own. MEASURED with a real
      // paused technician on the middle child: D1 and D3 were Cancelled, D2
      // refused, the parent was never deleted — and the operator was told
      // "Deletion aborted" after two children had already died. Measured the
      // other way round, with the PARENT blocked: both children were cancelled
      // to make room for a deletion that then refused.
      //
      // Neither state is one a person chose, and "abort" was never true —
      // there was nothing left to abort by the time the word was used.
      //
      // The whole act is now one transaction. The descendant list is NOT sent:
      // a client projection is not membership, so the authority derives the
      // family itself under the source's transition lock.
      //
      // FLOYD 2026-10-26 (DEFECT 1): `deleteTicketFamily` used to be loaded
      // HERE with `await import(...)`, in its own hashed chunk. A tab opened
      // before the 12:12 UTC deploy asked for deleteTicketFamily-DjyUFVD5.js
      // after it; the chunk no longer existed, the host answered with
      // index.html, the import threw, and the delete was never sent. It is a
      // static import now (top of file): the delete action ships inside the
      // menu that offers it.

      const now = new Date();
      const timestamp = now.toLocaleString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      const reasonLabels: Record<string, string> = {
        customer_canceled: 'Customer canceled',
        no_show: 'No show',
        ticket_mistake: 'Ticket made by mistake'
      };

      const { data: { user } } = await supabase.auth.getUser();

      // V-A-15: routed through mutateTicketTimerState. The transition
      // encapsulates the verbatim two-step pattern (best-effort close
      // any open ticket_timer_sessions row, then authoritative ticket
      // UPDATE with the FIX-R29-CRIT-3 location/timer-clear payload).
      // The Chronomancer session-close note and the FIX-R29-CRIT-3
      // payload are now invariants of `softDeleteTicketWithSessionClose`
      // — call-sites no longer duplicate the shape.
      //
      // assigned_tech_id is intentionally NOT preserved here. Migration
      // audit confirms no current trigger nulls it on status='Deleted'
      // (the only `tbs_on_ticket_status_change` trigger only invokes
      // business-state transitions). PostgREST UPDATE leaves un-keyed
      // columns unchanged, so the ticket's existing assigned_tech_id
      // is preserved by NOT writing the column. If a future migration
      // adds a trigger that nulls it, add a `preserveAssignedTechId`
      // option to the transition.
      // 8G.2: DELETION IS NOT A TIMER COMMAND.
      //
      // This used to call `softDeleteTicketWithSessionClose`, which closed
      // EVERY open timer session on the ticket and then tombstoned it.
      // Measured with two active technicians: live sessions 2 -> 0. A manager
      // deleting a ticket had never asserted "Tech A and Tech B stopped
      // working now", but the product asserted it for them.
      //
      // The server authority refuses while anyone is working, paused, or owed
      // an allocation review, and resolves nothing automatically — the
      // workflow is technician Stop, then allocation review, then delete. It
      // also asks where the vehicle goes if it is still in a bay rather than
      // silently clearing it.
      requestSent = true;
      const terminal = await deleteTicketFamily(
        ticketId,
        reasonLabels[reason] || reason,
        location?.kind ?? null,
        location?.spotId ?? null,
      );

      if (!isFamilyDeleteSettled(terminal.outcome)) {
        // FLOYD-5 — A QUESTION IS NOT A FAILURE.
        //
        // `LOCATION_REQUIRED` used to land here and `return`, so the product
        // asked "Where is the vehicle?" and then offered nowhere to say. An
        // in-bay ticket could not be deleted from the UI at all.
        //
        // It is a prompt, so it opens the prompt. The reason the operator
        // already chose is held across the round trip — making them pick it
        // again would charge them for answering a question we asked.
        //
        // `LOCATION_CONFLICT` comes back through here too: a spot somebody
        // else took is the same question again, not a dead end one step
        // later. Nothing was written either way, so re-asking is safe.
        if (shouldPromptForDeleteLocation(terminal.outcome)) {
          setPendingDeleteReason(reason);
          setShowDeleteLocationPicker(true);
          toast({
            title: terminal.outcome === 'LOCATION_CONFLICT'
              ? 'That spot is taken'
              : 'Where is the vehicle?',
            description: describeFamilyDeleteRefusal(terminal),
          });
          return;
        }

        // OCT20 WS-ROLL (FD-04): a rollover day is not refused into a dead end — the operator is guided to the
        // whole-repair action ("Delete Entire Rollover" / "Cancel"). Nothing was written by this refusal.
        if (terminal.outcome === 'ROLLOVER_CHAIN_MEMBER') {
          setPendingDeleteReason(null);
          setChainDeleteReason(reasonLabels[reason] || reason);
          return;
        }

        console.error('Delete refused:', terminal.outcome);
        setPendingDeleteReason(null);
        toast({
          // A refusal now means NOTHING was written — not even to a linked
          // ticket — so this no longer has to hedge about partial results.
          //
          // SUBMIT_FAILED is not a refusal: there was no server answer, and
          // the act may have committed with its response lost. Saying
          // "Nothing was deleted" there would be a claim nobody can make.
          // (A retry is safe — the authority answers ALREADY_APPLIED.)
          title: terminal.outcome === 'SUBMIT_FAILED' ? 'Delete not confirmed' : 'Nothing was deleted',
          description: describeFamilyDeleteRefusal(terminal),
          variant: 'destructive',
        });
        return;
      }

      // Settled. The held reason has done its job.
      setPendingDeleteReason(null);

      // A retry after a lost response recovers STATE but is not a second
      // human act: emitting the lifecycle event again would manufacture a
      // deletion that never happened.
      const firstTerminalization = didDeleteFamily(terminal.outcome);

      const result = { success: true } as { success: boolean; error?: string };
      if (!result.success) {
        console.error('Delete error:', result.error);
        toast({
          title: 'Error',
          description: result.error || 'Failed to delete ticket',
          variant: 'destructive',
        });
        return;
      }

      // FLOYD 2026-10-26 (DEFECT 1): from here on the ticket IS deleted. Every
      // follow-up below is derived, best-effort work; one that fails is
      // reported as what it is, never as "Failed to delete ticket".
      let followUpFailed = false;

      // OCIS-R67-FIX (HIGH-1): previously deletion wrote no timeline event —
      // tickets vanished with no audit trail. Emit a status_changed event so
      // the timeline preserves who deleted, when, and why.
      //
      // 8G.2B: gated on `firstTerminalization`. ALREADY_TERMINAL is a
      // successful recovery of STATE after a lost response, not a second
      // deletion — emitting here on a retry would write a second audit event
      // for one human act. The UI still moves on either way.
      if (firstTerminalization && ticketToDelete?.shop_id) {
        try {
          await logTicketEventToOcis({
            ticketId,
            shopId: ticketToDelete.shop_id,
            eventType: 'ticket_status_changed',
            content: `Ticket deleted (${reasonLabels[reason] || reason})`,
            authorUserId: user?.id,
            payload: {
              fromStatus: status,
              toStatus: 'Deleted',
              deletion_reason: reasonLabels[reason] || reason,
              reason_key: reason,
            },
          });
        } catch (err) {
          // The server's own audit rows are the record of the act; this is the derived timeline entry.
          console.error('[handleDelete] OCIS log error (the ticket IS deleted):', err);
          followUpFailed = true;
        }
      }

      // 8G.2D.4A: the descendants' own audit trail, preserved.
      //
      // `cancelTicketChain` wrote a Pensieve `ticket.cancelled` record and an
      // OCIS `ticket_status_changed` per descendant. Those records are the
      // timeline's only trace that a linked ticket ended, and OCIS-R67-FIX
      // added them precisely because cancellations used to vanish silently —
      // so they are kept rather than quietly dropped with the loop.
      //
      // What changed is WHEN. They are DERIVED, after-commit records of an act
      // the server has already performed, emitted from the authority's own
      // list of what it cancelled — not from a client-side guess, and never
      // before the transaction that made them true. Best-effort: a failed log
      // must not imply the deletion failed, because it did not.
      //
      // Gated on `firstTerminalization`, so a retry after a lost response
      // recovers state without writing a second history for one human act.
      //
      // 8G.3 owns terminal-event consolidation. This deliberately does not
      // invent a new event vocabulary; it preserves the existing one truthfully.
      if (firstTerminalization && ticketToDelete?.shop_id
          && terminal.cancelled.length > 0) {
        const nowISO = now.toISOString();
        await Promise.allSettled(terminal.cancelled.map(async (descendantId) => {
          try {
            await (supabase as any).from('pensieve_events').insert({
              shop_id: ticketToDelete.shop_id,
              ticket_id: descendantId,
              event_type: 'ticket.cancelled',
              session_id: crypto.randomUUID(),
              timestamp: nowISO,
              performed_by_user_id: user?.id || null,
              metadata: {
                reason: 'parent-deleted',
                cancelled_via: 'delete_ticket_family_if_quiescent',
                parent_ticket_id: ticketId,
              },
            });
          } catch (err) {
            console.error('[handleDelete] descendant Pensieve log error:', err);
          }
          try {
            await logTicketEventToOcis({
              ticketId: descendantId,
              shopId: ticketToDelete.shop_id,
              eventType: 'ticket_status_changed',
              content: `Ticket cancelled (parent ${ticketId} deleted)`,
              authorUserId: user?.id,
              payload: {
                toStatus: 'Cancelled',
                reason: 'parent-deleted',
                cancelled_via: 'delete_ticket_family_if_quiescent',
                parent_ticket_id: ticketId,
              },
            });
          } catch (err) {
            console.error('[handleDelete] descendant OCIS log error:', err);
          }
        }));
      }

      // If ticket was assigned to a tech, recalculate the schedule to cascade
      if (ticketToDelete?.assigned_tech_id && ticketToDelete?.start_time) {
        // R7-H-1: Use canonical scheduled_date; fallback via shop TZ, never UTC split
        const dateStr = ticketToDelete.scheduled_date ?? getShopDayLabel(ticketToDelete.start_time, shopTimezone);
        
        console.log('Triggering schedule cascade after deletion...', {
          techId: ticketToDelete.assigned_tech_id,
          date: dateStr,
          deletedStartTime: ticketToDelete.start_time
        });
        
        // Trigger server-side cascade (single authority for chaining)
        try {
          await triggerBoardCascade(ticketToDelete.shop_id, dateStr, ticketToDelete.assigned_tech_id);
          console.log('Schedule cascade complete');
        } catch (err) {
          console.error('[handleDelete] schedule re-walk failed (the ticket IS deleted):', err);
          followUpFailed = true;
        }
      }

      // Cross-device Dayboard sync
      try {
        void notifyDayboardInvalidated({
          reason: 'kebab-delete',
          shopId: ticketToDelete?.shop_id || shopId,
          ticketId,
          scheduledDate: ticketToDelete?.scheduled_date ?? selectedDayLabel,
        });
      } catch (err) {
        console.error('[handleDelete] board invalidation failed (the ticket IS deleted):', err);
        followUpFailed = true;
      }

      toast({
        title: 'Ticket Deleted',
        description: followUpFailed
          ? `Ticket deleted - ${reasonLabels[reason]}. The schedule could not be refreshed right away; the board will catch up on its next refresh.`
          : `Ticket deleted - ${reasonLabels[reason]}. Schedule updated.`,
      });

      if (onDelete) {
        onDelete(ticketId);
      }
    } catch (error: any) {
      console.error('Delete error:', error);
      if (!requestSent) {
        // Nothing was sent, so nothing was deleted. A chunk the browser could not load (an app updated while this
        // page was open) is said as such, with the one action that resolves it.
        const appUpdated = isChunkLoadError(error);
        toast({
          title: 'Nothing was deleted',
          description: appUpdated
            ? APP_UPDATED_MESSAGE
            : `${error?.message || 'The ticket could not be read'}. Try again.`,
          variant: 'destructive',
          ...(appUpdated ? { action: reloadToastAction() } : {}),
        });
      } else {
        // The request left; no answer came back. It may have committed — the retry is safe (ALREADY_APPLIED).
        toast({
          title: 'Delete not confirmed',
          description: 'The result could not be confirmed. Refresh the board to see whether the ticket is gone, and try again if it is still there.',
          variant: 'destructive',
        });
      }
    }
    setIsOpen(false);
  };

  /** OCT20 WS-ROLL (FD-04): after the server deleted every day of the repair as one act. */
  const handleChainDeleted = async (result: ChainDeleteResult, reason: string) => {
    if (result.outcome === 'CHAIN_DELETED') {
      const { data: { user } } = await supabase.auth.getUser();
      // Derived, after-commit timeline records (best-effort; the server's audit rows are the record of the act).
      await Promise.allSettled(result.deleted.map((d) => logTicketEventToOcis({
        ticketId: d.ticketId,
        shopId,
        eventType: 'ticket_status_changed',
        content: `Ticket deleted with its whole multi-day repair (${reason})`,
        authorUserId: user?.id,
        payload: { fromStatus: d.statusBefore, toStatus: 'Deleted', deletion_reason: reason,
          deleted_via: 'delete_rollover_chain_if_quiescent', root_ticket_id: ticketId },
      })));
    }
    // The lanes the repair occupied today or later are re-walked so their capacity is released on the boards.
    await Promise.allSettled(result.lanes.map((l) => triggerBoardCascade(shopId, l.scheduledDate, l.technicianId)));
    void notifyDayboardInvalidated({ reason: 'kebab-delete', shopId, ticketId, scheduledDate: selectedDayLabel });
    toast({
      title: 'Multi-day repair deleted',
      description: result.outcome === 'ALREADY_APPLIED'
        ? 'It was already deleted.'
        : `${result.deleted.length} day${result.deleted.length === 1 ? '' : 's'} deleted. Recorded time and history are kept.`,
    });
    if (onDelete) onDelete(ticketId);
  };

  const handleReactivate = async () => {
    // 8G.2C.3: DISABLED — BLOCKED ON 8G.3.
    //
    // This used to move Completed -> Active and clear completed_at,
    // completed_by and completed_by_tech_id through mutateTicketSchedule's
    // companion fields. MEASURED on a ticket completed through the canonical
    // 8G.1 authority: the row-level stamps vanished while FOUR jobs stayed
    // 'completed', along with the accepted allocations behind them, the
    // completion events and the performer provenance — a live ticket whose own
    // evidence said it was finished, with no event recording the reversal.
    //
    // Completion is a durable business act, not a status flag. Reopening the
    // SAME visit needs a defined reversal model: which terminal facts survive,
    // which are explicitly reversed, and what event proves it. That model does
    // not exist, and inventing one here would be inventing history.
    //
    // The server now refuses this transition outright (42501), so this is not
    // the only thing standing between a click and a contradiction — but a
    // button that always fails is worse than no button.
    // Core-100 R4 (CORE-AL): a completion is CORRECTED, by supersession, rather than reopened —
    // correct_ticket_completion (performer, measured job time) records the previous value and why.
    toast({
      title: 'Completed work is corrected, not reopened',
      description:
        'This visit is finished. Create a follow-up ticket for further work. A manager or owner can correct '
        + 'the measured job time (Edit timers); the correction is recorded and the original kept.',
    });
  };


  const handleAddJob = () => {
    if (onAddJob) {
      onAddJob(ticketId);
    }
    setIsOpen(false);
  };

  const handleSplit = () => {
    // Timer guard: don't allow split while timer is running
    if (timerStatus === 'running') {
      toast({
        title: 'Timer Running',
        description: 'Please pause or stop the timer before splitting this ticket.',
        variant: 'destructive',
      });
      setIsOpen(false);
      return;
    }
    
    if (onSplit) {
      onSplit(ticketId);
    }
    setIsOpen(false);
  };

  const handleMerge = () => {
    if (onMerge) {
      onMerge(ticketId);
    }
    setIsOpen(false);
  };

  const handleChangeDate = () => {
    setShowCalendarModal(true);
    setIsOpen(false);
  };

  const handleDateSelect = async (newDateStr: string) => {
    try {

      // Always fetch fresh ticket data to check parking lot status
      const { data: ticketData, error: fetchError } = await supabase
        .from('tickets')
        .select('assigned_tech_id, scheduled_tech_id, parking_lot, scheduled_date, shop_id')
        .eq('id', ticketId)
        .maybeSingle();
      
      if (fetchError) throw fetchError;
      
      const techId = ticketData?.assigned_tech_id;
      const isParkingLot = ticketData?.parking_lot === true;
      // oct20e (RF2): a checked-in car's lane is its plan (scheduled_tech_id — chainOwnership.ts rule B). Moving it to
      // another day is a schedule move, so it goes through reassign-ticket like any lane row; the database refuses a
      // direct date write on a row that holds a lane (SCHEDULE_DAY_SERVER_OWNED, 20270212110000).
      const planTechId = (ticketData as { scheduled_tech_id?: string | null } | null)?.scheduled_tech_id ?? null;
      
      console.log('[DATE-CHANGE] ticketId:', ticketId, 'techId:', techId, 'isParkingLot:', isParkingLot);

      // If ticket is in parking lot with no technician, just update the date directly
      // oct20e (RF2): ...unless it is a checked-in car with a plan (its lane) and not a rollover chain row: that is a
      // schedule move and falls through to reassign-ticket below, for its plan owner.
      const laneTechId = techId ?? (isParkingLot ? planTechId : null);
      const parkedWithPlan = isParkingLot && !techId && !!planTechId
        && !(await (await import('@/utils/rolloverChain')).isRolloverChainRow(ticketId));
      if (isParkingLot && !techId && !parkedWithPlan) {
        console.log('[DATE-CHANGE] Parking lot ticket - updating date directly');
        // V-A-1: route the parking-lot date change through the canonical
        // scheduling-mutation helper. The helper fires the same cascade
        // (scoped to the new date, all-tech since unassigned) that R38
        // FIX-7 added by hand below.
        // A ROLLOVER CHAIN ROW is never moved alone. The picked date is the
        // operator's intent: the chain authority re-plans this row and every
        // unresolved row after it from the first valid opportunity on/after it
        // (a closed day or holiday resolves to the next open day), never
        // before the part it follows has finished. Started parts are history
        // and do not move. Where the vehicle IS does not change.
        const { isRolloverChainRow, replanRolloverChainFromRow } = await import('@/utils/rolloverChain');
        if (await isRolloverChainRow(ticketId)) {
          const replan = await replanRolloverChainFromRow(
            ticketId,
            { date: newDateStr, min: 0 },
            { mayPullEarlier: true, label: 'kebab_change_date_parking_lot' },
          );
          if (!replan || replan.status === 'refused' || replan.status === 'conflict') {
            const why = replan?.status === 'refused'
              ? (replan.reason === 'row is history' ? 'this part has already started' : replan.reason)
              : 'the chain changed while re-planning — try again';
            throw new Error(`Could not move this rollover part: ${why}`);
          }
        } else {
          // No lane (no technician, no plan): the date is the ticket's label, written directly.
          const result = await mutateTicketSchedule(
            ticketId,
            { scheduled_date: newDateStr },
            { reason: 'kebab_change_date_parking_lot' }
          );
          if (!result.success) throw new Error(result.error || 'Failed to update ticket date');
        }

        // Log date change event
        const { data: { user } } = await supabase.auth.getUser();
        const { logDateChanged } = await import('@/utils/ticketEventLogger');
        await logDateChanged({
          ticketId,
          shopId: '', // Will be fetched in the logger if needed
          userId: user?.id || '',
           // R7-L-3: Log ticket's actual old date, not the board's selectedDayLabel
           oldDate: ticketData?.scheduled_date ?? getShopDayLabel(scheduledStartTime || '', shopTimezone),
          newDate: newDateStr,
        });
        
        await queryClient.invalidateQueries({ queryKey: ['tickets'] });
        await queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });

        // Cross-device Dayboard sync (parking-lot branch)
        void notifyDayboardInvalidated({
          reason: 'kebab-date-change',
          shopId: ticketData?.shop_id || shopId,
          ticketId,
          scheduledDate: newDateStr,
          metadata: { oldDate: ticketData?.scheduled_date ?? null },
        });

        toast({
          title: 'Date Changed',
          description: `Ticket rescheduled to ${newDateStr}`,
        });
        setShowCalendarModal(false);
        return;
      }

      // For tickets with assigned technicians (or a checked-in car's plan), use the edge function
      if (!laneTechId) {
        throw new Error('Cannot change date: ticket has no assigned technician');
      }

      // Use the edge function to perform a safe date move + cascade
      const { data, error } = await supabase.functions.invoke('reassign-ticket', {
        body: {
          ticketId,
          targetTechId: laneTechId,
          scheduledDate: newDateStr,
          manualOverride: true,
          moveReason: 'manual-kebab-date-change',
        }
      });
      if (error) throw error;

      // Log date change event
      const { data: { user } } = await supabase.auth.getUser();
      const { logDateChanged } = await import('@/utils/ticketEventLogger');
      await logDateChanged({
        ticketId,
        shopId: '', // Will be fetched in the logger if needed
        userId: user?.id || '',
        // R7-L-3: Log ticket's actual old date, not the board's selectedDayLabel
        oldDate: ticketData?.scheduled_date ?? getShopDayLabel(scheduledStartTime || '', shopTimezone),
        newDate: newDateStr,
      });

      // Invalidate queries so UI updates immediately (realtime will also sync)
      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
      await queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });

      // Cross-device Dayboard sync (assigned-tech branch)
      void notifyDayboardInvalidated({
        reason: 'kebab-date-change',
        shopId: ticketData?.shop_id || shopId,
        ticketId,
        scheduledDate: newDateStr,
        metadata: { oldDate: ticketData?.scheduled_date ?? null, techId },
      });

      toast({
        title: 'Date Changed',
        description: `Ticket rescheduled to ${newDateStr}`,
      });
      setShowCalendarModal(false);
    } catch (error: any) {
      console.error('[DATE-CHANGE] Error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to change ticket date',
        variant: 'destructive',
      });
    }
  };

  // Phase 9: Manual vehicle_present toggle removed
  // vehicle_present is now derived from location fields by the sync_vehicle_present trigger

  // Codex: Mark Picked Up (Incomplete) — vehicle leaves without all work done.
  //
  // Two-step: (1) route the pickup through the canonical move_vehicle_location
  // RPC so it gets the role check, the trigger-derived vehicle_present sync,
  // AND — critically — the explicit parking_lot=false write. A direct
  // .update({ parking_spot_id: null, bay_id: null, picked_up_at: now() })
  // here would NOT clear parking_lot, and since the sync_vehicle_present
  // trigger derives presence from `parking_lot OR parking_spot_id OR bay_id
  // OR timer_status`, a previously-true parking_lot would leave the picked-up
  // vehicle showing as "present" on Birdseye and the dayboard. (2) Then a
  // narrow status update to set the Paused state and pickup-reason.
  const handleMarkPickedUpIncomplete = async (pickupReason: string, expectedReturnDate: string | null, releasePauseIds: string[] = []) => {
    setIncompletePickupBusy(true);
    try {
      // 8G.2C: ONE ATOMIC AUTHORITY, AND NO FABRICATED PAUSE.
      //
      // This used to do TWO independent mutations, physical FIRST:
      //   1. move_vehicle_location(..., p_is_pickup: true)   <- committed alone
      //   2. mutateTicketTimerState({ kind: 'pauseTicketTimer' })
      //
      // Step 2 closed whatever open session it found BY TICKET, so recording
      // that a customer drove away could end a technician's measured work. And
      // because step 1 committed on its own, a failure in step 2 left the car
      // gone from the board with the ticket still claiming to be in progress.
      //
      // It also had to INVENT a pause. `tickets_lifecycle_active_paused_pickup_state`
      // forbids status='Paused' with any timer_status other than 'paused' —
      // measured: status='Paused' + timer_status='stopped' is rejected 23514 —
      // so having chosen 'Paused', the old code was FORCED to write
      // timer_status='paused', paused_at and paused_by for a pause nobody took.
      //
      // The server now does both facts in ONE transaction, refuses while any
      // technician is working, paused or owed a review, and records the
      // truthful status 'Blocked': the work cannot proceed because the vehicle
      // is gone, and nobody paused.
      const { markPickedUpIncomplete, isPickupSettled, didPickUp,
              needsPickupReload, describePickupRefusal } =
        await import('@/utils/ticketPickupLifecycle');
      // Oct 20 addendum (L2): the paused sessions the person confirmed in the dialog end with the departure.
      const pickup = await markPickedUpIncomplete(ticketId, pickupReason, expectedReturnDate, releasePauseIds);

      // 8G.2C.8: an UNKNOWN result is neither success nor refusal. The server
      // may well have committed, so re-firing the handover risks recording a
      // second departure for one car; and the copy used to say "Try again",
      // contradicting the outcome's own documentation. Reload authoritative
      // state and let the operator see what is true.
      if (needsPickupReload(pickup.outcome)) {
        toast({
          title: 'Pickup result unconfirmed',
          description: describePickupRefusal(pickup),
          variant: 'destructive',
        });
        await queryClient.invalidateQueries({ queryKey: ['tickets'] });
        await queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });
        return;
      }

      if (!isPickupSettled(pickup.outcome)) {
        toast({
          title: 'Not ready for pickup',
          description: describePickupRefusal(pickup),
          variant: 'destructive',
        });
        return;
      }

      // A retry recovers state but is not a second handover: no duplicate
      // reminder and no duplicate audit act.
      const firstPickup = didPickUp(pickup.outcome);

      // 8G.2C.9 §14 — THIS COMMENT USED TO SAY every notification below is
      // gated on `firstPickup`, and that a retry "must not cancel reminders
      // again, re-enqueue a follow-up, or re-arm an owner escalation".
      //
      // 8G.2C.8 measured that doctrine causing real harm: after a lost
      // response none of those ever ran, and the customer kept being reminded
      // about a car they had already driven away in. The DERIVED consequences
      // now converge on any settled state; only what must be exactly-once
      // stays behind `firstPickup`. The authoritative human-act record is the
      // server's own audit act (8G.2C.9 §11), written inside the pickup
      // transaction.
      // CANONICAL shop from the server, resolved under the ticket transition lock.
      // The React context value can be stale, and durable attribution — a
      // reminder, an owner escalation, a timeline event — must not depend on
      // what the browser believed the active shop was.
      // NO FALLBACK. The server resolves and authorizes the ticket's shop
      // under the transition lock; the React context value can be stale or
      // simply belong to a different shop the user has since switched to.
      // Attributing a reminder, an owner escalation or a timeline event to a
      // caller-supplied shop is how durable records end up in the wrong
      // tenant. If a settled outcome somehow carries no shop, every durable
      // side effect is skipped and only the UI refreshes.
      const pickupShopId = pickup.shopId ?? null;

      // 8G.2C.3: cancel-reminders was gated on `firstPickup` ALONE while the
      // comment above claimed every durable effect was skipped without a
      // canonical shop. It was not, and the test written to catch that only
      // asserted that SOME later block carried the combined gate — so it
      // passed over the one call that did not.
      //
      // Cancelling a customer's reminders is a durable, shop-attributed act
      // like the rest, so it moves inside the same gate.
      // ── DERIVED CONVERGENCE, SEPARATED FROM THE HUMAN ACT ──────────────
      //
      // This block used to be gated on `firstPickup`, which meant a lost
      // response left the customer permanently wrong. MEASURED: the server
      // commits, the browser never sees the reply, the retry answers
      // ALREADY_PICKED_UP, `firstPickup` is false — and cancel-reminders, the
      // follow-up, the owner escalation and the cascade ALL ran ZERO times,
      // forever. Ticket state recovered; the customer kept getting reminders
      // about a car they had already driven away in.
      //
      // "The customer took the vehicle once" and "the system's consequences of
      // that departure are in place" are different claims. The first must be
      // exactly-once — it is the server's own audit act. The second must
      // CONVERGE, and may run on any confirmed settled state.
      //
      // 8G.2C.10 CORRECTION. This comment used to justify that with: the
      // enqueue UPSERTs on a UNIQUE constraint, therefore repeating is safe.
      // 8G.2C.9 disproved my own inference — a unique key gives IDENTITY
      // uniqueness and says nothing about state, and the writer was rewriting
      // `send_at` and resurrecting sent/canceled rows underneath it.
      //
      // What makes repetition safe is the measured DURABLE BEHAVIOUR, pinned in
      // `pickup_reminder_convergence_proof.sql`: create-if-absent leaves an
      // existing row's schedule, status and delivery history alone; the
      // cancellation scope excludes these two consequences; and a second pass
      // changes no row, no schedule and no history.

      // 8G.2C.10 §9: each consequence is OBSERVED, not assumed from the
      // absence of a thrown exception. `functions.invoke` RESOLVES with
      // `{ error }` on failure, so a `.catch()` never runs and the old
      // `!result.error` test answered "did the request execute", never "is
      // the consequence in place".
      let consequences: PickupConsequences | null = null;

      if (isPickupSettled(pickup.outcome) && pickupShopId && pickup.pickedUpAt) {
        // 8G.2C.9 §7: schedules anchor to the CANONICAL departure instant, not
        // to whenever this code runs. A recovery six hours after a lost
        // response used to turn "24 hours after pickup" into 30 hours after
        // pickup, silently, every retry.
        const pickedUpMs = new Date(pickup.pickedUpAt).getTime();

        // 8G.2C.9 §9: POSITIVE scope. An unscoped cancel took every queued row
        // on the ticket, including the two consequences created immediately
        // below — measured: on a second pass both were cancelled, two synthetic
        // cancellation-log rows were written naming them, and the next enqueue
        // requeued them.
        const suppression = await supabase.functions.invoke('cancel-reminders', {
          body: {
            ticketId,
            reason: 'picked_up_incomplete',
            reminderTypes: PRE_PICKUP_REMINDER_TYPES,
          },
        }).catch(err => { console.warn('[TicketKebab] cancel-reminders error:', err); return { error: err }; });

        // CREATE-IF-ABSENT: an existing consequence keeps its schedule, status
        // and delivery history, and the response reports which of those it is.
        const followup = await supabase.functions.invoke('enqueue-reminders', {
          body: { ticketId, shopId: pickupShopId, type: 'picked_up_followup', targetRole: 'advisor', sendAt: new Date(pickedUpMs + 24 * 60 * 60 * 1000).toISOString() },
        }).catch(err => { console.warn('[TicketKebab] enqueue advisor followup error:', err); return { error: err }; });

        const escalation = await supabase.functions.invoke('enqueue-reminders', {
          body: { ticketId, shopId: pickupShopId, type: 'escalation', targetRole: 'owner', sendAt: new Date(pickedUpMs + 5 * 24 * 60 * 60 * 1000).toISOString() },
        }).catch(err => { console.warn('[TicketKebab] enqueue owner escalation error:', err); return { error: err }; });

        // 8G.2C.10 §8: the cascade resolves `{ ok }` rather than throwing, and
        // its result was being discarded entirely.
        const cascade = await triggerBoardCascade(pickupShopId, selectedDayLabel)
          .catch(err => { console.warn('[TicketKebab] cascade error:', err); return { ok: false, error: String(err) }; });

        consequences = {
          suppression: classifySuppression(suppression),
          followup: classifyEnqueue(followup),
          escalation: classifyEnqueue(escalation),
          cascade: classifyCascade(cascade),
        };
      }

      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
      await queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });

      // Every mounted surface, here and on the shop's other devices, re-reads
      // (FOH queue, Dayboard, Calendar). New pickups only — a retry that
      // answers ALREADY_PICKED_UP refreshed this board above and stops there.
      notifyPickupChanged({
        isNewPickup: firstPickup, ticketId, shopId: pickupShopId,
        pickedUpAt: pickup.pickedUpAt, act: 'incomplete',
      });

      // OCIS-R69-FIX (HIGH-5): mark-picked-up-incomplete previously left no
      // timeline event — the ticket flipped to Paused with pickup metadata
      // but OCIS had no record of the pickup itself. Emit status_changed.
      // 8G.2C.11 CORRECTION: this said "for the same reason the reminders
      // are", which stopped being true in 8G.2C.8 — the reminders and the
      // cascade are DELIBERATELY not `firstPickup`-gated, because gating them
      // left a lost response with no reconciliation at all.
      //
      // The real reason is narrower and unrelated: this event is a projection
      // of the singular HUMAN ACT, so a retried transport must not write a
      // second pickup into the timeline. Derived reconciliation may repeat;
      // the record of the act may not.
      if (firstPickup && pickupShopId) {
        const { data: { user } } = await supabase.auth.getUser();
        logTicketEventToOcis({
          ticketId,
          shopId: pickupShopId,
          eventType: 'ticket_status_changed',
          // 8G.2C.9 §13: the content used to append "— reminders queued". The
          // enqueue calls catch their errors and continue, so the pickup can
          // commit while both reminders fail; the timeline would then assert
          // something that did not happen. A human-act record states the
          // HUMAN ACT. Derived-effect status belongs to operational feedback,
          // which the toast below now reports from actual results.
          content: 'Vehicle picked up (incomplete)',
          authorUserId: user?.id,
          payload: {
            fromStatus: status,
            // 8G.2C: 'Blocked', not 'Paused'. Nobody paused — the vehicle left.
            toStatus: 'Blocked',
            reason: 'picked_up_incomplete',
            picked_up: true,
            pickup_reason: pickupReason,
            expected_return_date: expectedReturnDate,
          },
        }).catch(err => console.error('[TicketKebab] PickedUpIncomplete OCIS log error:', err));
      }

      // 8G.2C.9 §13: the copy reports what ACTUALLY happened.
      //
      // It used to say "Follow-up reminders queued" whenever there was a shop,
      // but the enqueue calls swallow their errors and continue — so the
      // pickup could commit with both reminders having failed and the operator
      // would be told they were scheduled. The flags below come from the
      // invoke results.
      // 8G.2C.10 §10: every positive claim below is backed by an OBSERVED
      // state. "Scheduled" is said only when a reminder is actually queued;
      // an already-sent one is reported as sent, and one an operator cancelled
      // is reported as left cancelled rather than silently re-armed.
      const pickupTitle = firstPickup
        ? 'Marked Picked Up (Incomplete)'
        : 'Already recorded as picked up';
      toast({
        title: pickupTitle,
        description: !consequences
          // The pickup COMMITTED — say so — but nothing derived ran, because
          // there was no canonical shop or instant to attribute it to.
          ? 'Pickup recorded. Follow-up reminders could not be scheduled — reopen the ticket to retry.'
          : describeConsequences(consequences),
        variant: consequences && allConsequencesOk(consequences) ? undefined : 'destructive',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to mark as picked up',
        variant: 'destructive',
      });
    } finally {
      setIncompletePickupBusy(false);
      setShowIncompletePickupDialog(false);
    }
    setIsOpen(false);
  };

  // Mark as Contacted handler (Lane 3 confirmation workflow)
  // Now shows confirmation dialog immediately after marking as contacted
  const handleMarkContacted = async () => {
    try {
      const { error } = await supabase
        .from('tickets')
        .update({ confirmation_status: 'contacted' })
        .eq('id', ticketId);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
      await queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });

      // Close dropdown and show confirmation dialog
      setIsOpen(false);
      setShowConfirmDialog(true);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update confirmation status',
        variant: 'destructive',
      });
      setIsOpen(false);
    }
  };

  // R38 FIX-9: Deduplicated — handleConfirmAppointment handles both dialog and direct confirm

  // Confirm Appointment handler (Lane 3 confirmation workflow)
  const handleConfirmAppointment = async () => {
    try {
      const { error } = await supabase
        .from('tickets')
        .update({ 
          confirmation_status: 'confirmed',
          is_locked: true 
        })
        .eq('id', ticketId);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
      await queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });

      toast({
        title: 'Appointment Confirmed',
        description: 'Ticket is now locked and confirmed',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to confirm appointment',
        variant: 'destructive',
      });
    }
    setIsOpen(false);
    setShowConfirmDialog(false);
  };

  // Lifecycle v2 — slice 3. Custody handoff actions, flag-gated. The
  // query is enabled only when the flag is on, so flag-off rendering
  // makes no network calls and adds zero layout shift.
  const custodyHandoffEnabled = useFeatureFlag('custodyHandoff');
  const { data: custodyRow } = useTicketCustody(
    custodyHandoffEnabled ? ticketId : null,
  );
  const custodyMutation = useTicketCustodyMutation();
  // No row yet ⇒ default position (BOH / with_tech), matching the
  // lazy-init in _apply_custody_transition.
  const custodyStatus = custodyRow?.custody_status ?? 'with_tech';
  // CORE-AK: FOH acceptance is offered only to people who act as front office.
  const custodyAction = primaryCustodyAction(custodyStatus, { actsAsFrontOffice: !!canReassignAdvisor });
  // Icon reflects current custody owner: BOH/tech → Wrench, FOH/advisor → UserRound.
  // Matches the TicketCustodyBadge mapping (owner === 'FOH' ? UserRound : Wrench).
  const CustodyIcon = custodyStatus === 'with_advisor' || custodyStatus === 'pending_advisor_accept'
    ? UserRound
    : Wrench;

  const handleCustodyTransition = async () => {
    if (!custodyAction) return;
    setIsOpen(false);
    // Batch 1 (FOH timer handoff): kick actions now route through the
    // structured reason picker so `ticket_custody_events.reason` is
    // populated with a canonical value and metadata carries
    // trigger='manual'. Accept actions (foh_accept / boh_accept) still
    // fire immediately — they are not handoffs and have no reason
    // semantics.
    if (custodyAction.promptReason) {
      setShowCustodyReasonDialog(true);
      return;
    }
    try {
      await custodyMutation.mutateAsync({
        ticketId,
        eventKind: custodyAction.kind,
        reason: null,
      });
    } catch (e) {
      console.error('[custody] transition failed', e);
      toast({
        title: 'Custody transition failed',
        description: (e as Error)?.message || 'Unknown error — check console.',
        variant: 'destructive',
      });
    }
  };

  const handleCustodyReasonConfirm = async (
    result: CustodyHandoffReasonDialogResult,
  ) => {
    if (!custodyAction) return;
    const metadata = buildCustodyHandoffMetadata({
      trigger: 'manual',
      reason: result.reason,
      reasonLabel: result.reasonLabel,
      notes: result.notes,
      sourceComponent: 'TicketKebabMenu',
    });
    try {
      await custodyMutation.mutateAsync({
        ticketId,
        eventKind: custodyAction.kind,
        reason: result.reason,
        metadata,
      });
      setShowCustodyReasonDialog(false);
    } catch (e) {
      console.error('[custody] transition failed', e);
      toast({
        title: 'Custody transition failed',
        description: (e as Error)?.message || 'Unknown error — check console.',
        variant: 'destructive',
      });
    }
  };

  const isCompleted = status === 'Completed';

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost"
            size="sm"
            // 32px is a comfortable mouse target and a poor thumb target, and
            // this button is the door to the only touch path for moving a
            // ticket. Widened for coarse pointers only, so desktop card
            // density is unchanged by construction rather than by breakpoint.
            className="h-8 w-8 p-0 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11"
            onClick={(e) => e.stopPropagation()}
            data-testid="ticket-kebab-menu"
            aria-label="Ticket options menu"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-48"
          data-testid="ticket-kebab-dropdown"
          data-coachmark="ticket-kebab-dropdown"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {isCompleted ? (
            <>
              {canRestoreTickets && (
                <DropdownMenuItem onClick={handleReactivate}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Reactivate
                </DropdownMenuItem>
              )}
            </>
          ) : (
            <>
              
              {canEditTickets && jobCount >= 2 && !isSplitTicket && onSplit && (
                <DropdownMenuItem onClick={handleSplit} data-testid="split-ticket-menu-item">
                  <Scissors className="mr-2 h-4 w-4" />
                  Split Ticket
                </DropdownMenuItem>
              )}

              {canEditTickets && isSplitTicket && onMerge && (
                <DropdownMenuItem onClick={handleMerge}>
                  <Merge className="mr-2 h-4 w-4" />
                  Merge Split Parts
                </DropdownMenuItem>
              )}
              
              {canEditTickets && (
                <DropdownMenuItem onClick={handleChangeDate}>
                  <Calendar className="mr-2 h-4 w-4" />
                  Change Date
                </DropdownMenuItem>
              )}

              
              {/* Phase 9: Manual vehicle_present toggle removed - derived from location fields */}
              
              {/* Codex: Picked-up-incomplete action.
                  DMC-D3c-3: canonical deleted-state check via helper. The
                  TicketKebabMenuProps interface currently only exposes
                  `status`, not `deletedAt`, so the helper sees only the
                  status rep — semantically identical to the pre-D3c-3
                  check at this site. To get the deleted_at defense-in-
                  depth here, a follow-up would add an optional
                  `deletedAt?: string | null` prop and thread it from the
                  ~12 caller sites. Out of this round's surgical scope. */}
              {canEditTickets && status !== 'Completed' && !isTicketDeleted({ status }) && (
                <DropdownMenuItem onClick={() => setShowIncompletePickupDialog(true)}>
                  <PackageX className="mr-2 h-4 w-4" />
                  Mark Picked Up (Incomplete)
                </DropdownMenuItem>
              )}

              {/* OCT28 #13: a parked repair waiting on an outside dependency — the front office marks it ready. The item asks the
                  server for the ticket's reason itself (TicketCard, byte-pinned, does not pass timer state here). */}
              {isInParkingLot && canEditTickets && <InterruptedRepairReadyItem ticketId={ticketId} />}

              {/* Oct 20 (D-9 / FD-01): the PHYSICAL move, through the one location authority. */}
              {canRelocateVehicles && !isTicketDeleted({ status }) && status !== 'Completed' && (
                <DropdownMenuItem onClick={() => setShowMoveVehicleDialog(true)} data-testid="kebab-move-vehicle">
                  <Move className="mr-2 h-4 w-4" />
                  Move Vehicle…
                </DropdownMenuItem>
              )}

              {canEditTickets && !isInParkingLot && (
                <DropdownMenuItem onClick={handleMoveToParkingLot} data-testid="kebab-move-to-parking-lot">
                  <Inbox className="mr-2 h-4 w-4" />
                  {/* D-9: a scheduling act (unassign + backlog); the vehicle does not move. */}
                  Move to Parking Lot (unschedule)
                </DropdownMenuItem>
              )}
              
              {canAssignTickets && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Move className="mr-2 h-4 w-4" />
                    Move Ticket
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto" data-coachmark="move-job-submenu">
                    {availableTechnicians.map(tech => (
                      <DropdownMenuItem 
                        key={tech.id}
                        onClick={() => handleMoveTechnician(tech.id, tech.name)}
                      >
                        <User className="mr-2 h-4 w-4" />
                        {tech.name}
                      </DropdownMenuItem>
                    ))}
                    {availableTechnicians.length === 0 && (
                      <DropdownMenuItem disabled>
                        {presentTechnicians.length > 0
                          ? 'No present technician can do this work'
                          : 'No available technicians'}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}

              {/* Team Transfer (Enterprise multi-team only) */}
              {isMultiTeamEnabled && canAssignTickets && (
                <DropdownMenuItem onClick={() => {
                  setIsOpen(false);
                  setShowTransferDialog(true);
                }}>
                  <ArrowRightLeft className="mr-2 h-4 w-4" />
                  Transfer to Team
                </DropdownMenuItem>
              )}

              {/* Lane 3 Confirmation Actions */}
              {confirmationStatus === 'needs_contact' && canEditTickets && (
                <DropdownMenuItem onClick={handleMarkContacted}>
                  <Phone className="mr-2 h-4 w-4" />
                  Mark as Contacted
                </DropdownMenuItem>
              )}
              {confirmationStatus === 'contacted' && canEditTickets && (
                <DropdownMenuItem onClick={handleConfirmAppointment}>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Confirm Appointment
                </DropdownMenuItem>
              )}
            </>
          )}
          
          {custodyHandoffEnabled && !isCompleted && custodyAction && (
            <DropdownMenuItem
              onClick={handleCustodyTransition}
              disabled={custodyMutation.isPending}
              data-testid={`custody-action-${custodyAction.kind}`}
            >
              <CustodyIcon className="mr-2 h-4 w-4" />
              {custodyAction.label}
            </DropdownMenuItem>
          )}

          {/* Slice 5D-1: Explicit advisor reassignment. Visible only when
              the custodyHandoff flag is on, the ticket currently sits in
              FOH custody (so reassign_within_foh is a legal transition),
              the actor has an advisor-capable role, and the ticket is
              not completed. The dialog itself defends in depth — the
              edge function + RPC remain authoritative. */}
          {custodyHandoffEnabled &&
            !isCompleted &&
            canReassignAdvisor &&
            custodyRow?.custody_owner === 'FOH' && (
              <DropdownMenuItem
                onClick={() => {
                  setIsOpen(false);
                  setShowReassignAdvisorDialog(true);
                }}
                data-testid="custody-action-reassign_within_foh"
              >
                <UserCog className="mr-2 h-4 w-4" />
                Reassign Advisor
              </DropdownMenuItem>
            )}

          {canDeleteTickets && (
            <DropdownMenuItem
              onClick={() => {
                setIsOpen(false);
                setShowDeletionDialog(true);
              }}
              className="text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <DeletionReasonDialog 
        open={showDeletionDialog}
        onOpenChange={setShowDeletionDialog}
        onConfirm={handleDelete}
      />

      {/* FLOYD-5 — the answer surface for "Where is the vehicle?".
          The SHARED picker, in selection-only mode: it reports the choice and
          writes nothing, because the delete authority performs the move as
          part of the one atomic act. `requireExplicitSelection` keeps the
          General Lot from being preselected — ending a visit is not the
          moment to let a default answer a question on someone's behalf. */}
      <ParkingSpotPickerDialog
        isOpen={showDeleteLocationPicker}
        shopId={shopId}
        ticketId={ticketId}
        selectionOnly
        requireExplicitSelection
        onClose={() => {
          // Cancelling deletes NOTHING. The first attempt was refused, so
          // there is no half-finished act to unwind — only a held reason.
          setShowDeleteLocationPicker(false);
          setPendingDeleteReason(null);
        }}
        onSelect={(spotId) => {
          const reason = pendingDeleteReason;
          setShowDeleteLocationPicker(false);
          if (!reason) return;
          void handleDelete(reason, toTerminalLocation(spotId));
        }}
      />

      {chainDeleteReason !== null && (
        <RolloverChainDeleteDialog
          open
          onOpenChange={(o) => { if (!o) setChainDeleteReason(null); }}
          ticketId={ticketId}
          shopId={shopId}
          initialReason={chainDeleteReason}
          onDeleted={(r, why) => { void handleChainDeleted(r, why); }}
        />
      )}

      <CalendarModal
        open={showCalendarModal}
        onOpenChange={setShowCalendarModal}
        currentDate={currentDate}
        onDateSelect={handleDateSelect}
      />

      <EditTicketDetailsModal
        isOpen={showEditDetailsModal}
        onClose={() => setShowEditDetailsModal(false)}
        ticketId={ticketId}
        initialTicketType={ticketType}
        initialScheduledStartTime={scheduledStartTime}
        initialDueOut={dueOut}
        initialIsComeback={isComeback}
        predictedMinutes={predictedMinutes}
      />

      {/* Lane 3 Confirmation Dialog - appears after Mark as Contacted */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              Customer has been contacted. Would you like to confirm this appointment now?
              This will lock the ticket to prevent automated scheduling changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not Yet</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmAppointment}>
              Yes, Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Team Transfer Dialog */}
      <TicketTransferDialog
        open={showTransferDialog}
        onOpenChange={setShowTransferDialog}
        ticketId={ticketId}
        currentTeamId={teamId}
        onTransferComplete={() => {
          queryClient.invalidateQueries({ queryKey: ['tickets'] });
          queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });
        }}
      />

      <IncompletePickupDialog
        open={showIncompletePickupDialog}
        subject={null}
        shopTimezone={shopTimezone}
        busy={incompletePickupBusy}
        ticketId={ticketId}
        onConfirm={(r, d, ids) => { void handleMarkPickedUpIncomplete(r, d, ids); }}
        onCancel={() => setShowIncompletePickupDialog(false)}
      />
      <FohMoveVehicleDialog
        open={showMoveVehicleDialog}
        ticketId={showMoveVehicleDialog ? ticketId : null}
        shopId={shopId}
        onClose={() => setShowMoveVehicleDialog(false)}
        onMoved={(id) => {
          toast({ title: 'Vehicle moved' });
          queryClient.invalidateQueries({ queryKey: ['tickets'] });
          queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });
          queryClient.invalidateQueries({ queryKey: ['ticket-location-history', id] });
          void notifyDayboardInvalidated({ reason: 'kebab-move-vehicle', shopId, ticketId: id });
        }}
      />

      <ParkingLotReasonDialog
        open={showParkingLotReasonDialog}
        onOpenChange={setShowParkingLotReasonDialog}
        onConfirm={handleParkingLotReasonConfirm}
        onCancel={() => setShowParkingLotReasonDialog(false)}
      />

      {/* Batch 1 (FOH timer handoff): manual kick reason picker. Only
          mounted while custodyHandoff is on AND there is a kick action
          to perform; opens via handleCustodyTransition. */}
      {custodyHandoffEnabled && custodyAction?.promptReason && (
        <CustodyHandoffReasonDialog
          open={showCustodyReasonDialog}
          onOpenChange={setShowCustodyReasonDialog}
          trigger="manual"
          eventKind={custodyAction.kind}
          isPending={custodyMutation.isPending}
          onConfirm={handleCustodyReasonConfirm}
        />
      )}

      {/* Slice 5D-1: Reassign Advisor dialog. Mounted lazily — the inner
          useFohCapableMembers query is `enabled: false` until the dialog
          is opened AND custodyHandoff is on, so off-state shops pay
          zero network cost. shopId presence is required by the dialog;
          when missing we render nothing (callers without an active shop
          would not have reached this kebab anyway). */}
      {custodyHandoffEnabled && shopId && (
        <ReassignAdvisorDialog
          open={showReassignAdvisorDialog}
          onOpenChange={setShowReassignAdvisorDialog}
          ticketId={ticketId}
          shopId={shopId}
          currentHolderUserId={custodyRow?.holder_user_id ?? null}
        />
      )}
    </>
  );
};
